import { useMemo } from 'react';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';
import { useReviewSummary } from '../lib/queries';

// Upcoming-workload heatmap. Each cell is one calendar day; 7 rows (Sun–Sat)
// × 16 columns (weeks). Leftmost column starts on the Sunday that contains
// today, so today lands somewhere in column 0 and the grid extends 16 weeks
// into the future. Colour scales with how many cards are due that day.
const WEEKS = 16;
const DAYS_PER_WEEK = 7;

const levelBg: Record<number, string> = {
  0: colors.line,
  1: 'rgba(14, 140, 74, 0.22)',
  2: 'rgba(14, 140, 74, 0.45)',
  3: 'rgba(14, 140, 74, 0.72)',
  4: colors.good,
};

function level(count: number, max: number): number {
  if (count === 0) return 0;
  if (max <= 1) return count > 0 ? 4 : 0;
  const ratio = count / max;
  if (ratio >= 0.75) return 4;
  if (ratio >= 0.5) return 3;
  if (ratio >= 0.25) return 2;
  return 1;
}

export function Heatmap() {
  const { t } = useTranslation('library');
  const summaryQuery = useReviewSummary();
  const forecast = summaryQuery.data?.forecast ?? [];

  const { cells, max, weekLabels } = useMemo(() => {
    const byDay = new Map<string, number>();
    for (const r of forecast) byDay.set(r.day, r.count);

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    // Start at the Sunday of this week so the first column is a full week.
    const dow = today.getUTCDay();
    const gridStart = new Date(today.getTime() - dow * 86400000);

    const flat: { iso: string; count: number; isToday: boolean; inFuture: boolean }[] = [];
    let max = 0;
    for (let week = 0; week < WEEKS; week++) {
      for (let day = 0; day < DAYS_PER_WEEK; day++) {
        const d = new Date(gridStart.getTime() + (week * DAYS_PER_WEEK + day) * 86400000);
        d.setUTCHours(0, 0, 0, 0);
        const iso = d.toISOString().slice(0, 10);
        const isToday = d.getTime() === today.getTime();
        const inFuture = d.getTime() >= today.getTime();
        const count = inFuture ? byDay.get(iso) ?? 0 : 0;
        if (count > max) max = count;
        flat.push({ iso, count, isToday, inFuture });
      }
    }

    // Month labels: show the month name at the first column of each month.
    const weekLabels: { col: number; label: string }[] = [];
    let lastMonth = -1;
    for (let week = 0; week < WEEKS; week++) {
      const firstDay = new Date(
        gridStart.getTime() + week * DAYS_PER_WEEK * 86400000,
      );
      const month = firstDay.getUTCMonth();
      if (month !== lastMonth) {
        weekLabels.push({
          col: week,
          label: firstDay.toLocaleDateString(undefined, { month: 'short' }),
        });
        lastMonth = month;
      }
    }

    return { cells: flat, max, weekLabels };
  }, [forecast]);

  const totalUpcoming = forecast.reduce((s, r) => s + r.count, 0);
  const peakDay = forecast.reduce(
    (best, r) => (r.count > best.count ? r : best),
    { day: '', count: 0 } as { day: string; count: number },
  );

  return (
    <div>
      <h3
        style={{
          fontWeight: 600,
          letterSpacing: '-0.01em',
          fontSize: 22,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {t('heatmap_title')}{' '}
          <em style={{ fontStyle: 'italic', color: colors.ink3 }}>·</em>{' '}
          <span style={{ fontVariantNumeric: 'tabular-nums', color: colors.ink3, fontWeight: 500 }}>
            {totalUpcoming}
          </span>
        </span>
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 11,
            color: colors.ink3,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 400,
          }}
        >
          heatmap
        </span>
      </h3>
      <div
        style={{
          background: colors.surface2,
          border: `1px solid ${colors.line2}`,
          borderRadius: 10,
          padding: 18,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${WEEKS}, 1fr)`,
            gap: 3,
            marginBottom: 6,
            fontSize: 9,
            color: colors.ink3,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontVariantNumeric: 'tabular-nums',
            minHeight: 12,
          }}
        >
          {Array.from({ length: WEEKS }).map((_, col) => {
            const label = weekLabels.find((l) => l.col === col)?.label ?? '';
            return <span key={col}>{label}</span>;
          })}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${WEEKS}, 1fr)`,
            gridTemplateRows: `repeat(${DAYS_PER_WEEK}, 1fr)`,
            gridAutoFlow: 'column',
            gap: 3,
          }}
        >
          {cells.map((c, i) => (
            <span
              key={i}
              title={`${c.iso} · ${c.count}`}
              style={{
                aspectRatio: '1 / 1',
                background: c.inFuture ? levelBg[level(c.count, max)] : 'transparent',
                border: c.inFuture ? 'none' : `1px dashed ${colors.line}`,
                borderRadius: 2,
                outline: c.isToday ? `1.5px solid ${colors.accent}` : 'none',
                outlineOffset: 1,
              }}
            />
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 10,
            color: colors.ink3,
            letterSpacing: '0.06em',
            fontVariantNumeric: 'tabular-nums',
            textTransform: 'uppercase',
            marginTop: 12,
          }}
        >
          <span>
            {peakDay.count > 0
              ? `peak ${peakDay.count} on ${new Date(`${peakDay.day}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
              : ''}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('heatmap_legend_less')}
            {[0, 1, 2, 3, 4].map((l) => (
              <span
                key={l}
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: levelBg[l],
                  display: 'inline-block',
                }}
              />
            ))}
            {t('heatmap_legend_more')}
          </span>
        </div>
      </div>
    </div>
  );
}
